import { useEffect, useState } from 'react';
import { trim } from 'lodash';
import { useDebouncedCallback } from 'use-debounce';

import { extractCustomFormatInsights } from 'src/graphql/format/extractCustomFormatInsights';
import { isAbortError } from 'src/lib/errors';
import { useErrorHandler } from 'src/services/useErrorHandler';

const DEFAULT_DEBOUNCE_TIMEOUT = 500;

// Custom hook to handle multiple format evaluations
export const useMultipleFormatInsights = ({ customerId, customFormatIds, html }, hookOptions) => {
  const handleError = useErrorHandler();

  const disableRequests = hookOptions?.disabled || false;
  const debounceTimeout = hookOptions?.debounceTimeout || DEFAULT_DEBOUNCE_TIMEOUT;
  const refreshKey = hookOptions?.refreshKey || 0;

  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(false);

  /**
   * Debounced "fetch" function that handles multiple format IDs
   */
  const fetchData = useDebouncedCallback(async (requestParams, requestOptions) => {
    try {
      const { customerId, customFormatIds, html } = requestParams;

      // Make separate GraphQL queries for each format ID
      const promises = customFormatIds.map(async (customFormatId) => {
        try {
          const result = await extractCustomFormatInsights(
            { customerId, customFormatId, html },
            requestOptions
          );
          return { customFormatId, result };
        } catch (error) {
          if (isAbortError(error)) {
            return { customFormatId, result: null };
          }
          handleError(error);
          return { customFormatId, result: null };
        }
      });

      // Wait for all queries to complete
      const allResults = await Promise.all(promises);

      // Filter out failed results and combine violations
      const validResults = allResults.filter(({ result }) => result !== null);

      setResults(validResults);
    } catch (error) {
      if (isAbortError(error)) {
        return;
      }
      handleError(error);
    } finally {
      setLoading(false);
    }
  }, debounceTimeout);

  /**
   * React to input parameters changes
   */
  useEffect(() => {
    // Clear and do not query, if host component doesn't want it now
    if (disableRequests) {
      setResults([]);
      return;
    }

    // Return early, if no "html" to analyze was provided
    if (trim(html).length === 0) {
      setResults([]);
      return;
    }

    // Return early, if no customFormatIds were provided
    if (!customFormatIds || customFormatIds.length === 0) {
      setResults([]);
      return;
    }

    // Mark as "loading" even when we wait for "debounced" request to fire
    setLoading(true);

    // Schedule debounced request
    const abortController = new AbortController();

    fetchData(
      { customerId, customFormatIds, html },
      {
        abortSignal: abortController.signal,
      }
    );

    // Clean up possible pending requests when we want to schedule new request or component unmounts
    return () => {
      abortController.abort();
      setLoading(false);
    };
  }, [disableRequests, fetchData, customerId, customFormatIds, html, refreshKey]);

  // Return separate objects for each custom format
  const formatResults = results
    .filter(
      (f, index, self) => index === self.findIndex((t) => t.customFormatId === f.customFormatId)
    )
    .map(({ customFormatId, result }) => ({
      formatId: customFormatId,
      violations: result.violations || [],
      fixedHtml: result.fixedHtml,
    }));

  return {
    formatResults,
    loading,
  };
};
